import React, { useState } from 'react';
import './DeleteAccount.css';
import ConfirmationDialog from '../../../funnel/ConfirmationDialog/ConfirmationDialog';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { deleteUserAccount } from '../../../api/productService';
import { useAuthStore } from '../../../store/useAuthStore';
import { useToast } from '../../../hooks/useToast';
import { useTranslation } from 'react-i18next';

const DeleteAccount: React.FC = () => {
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const logout = useAuthStore((state) => state.logout);
  const { showSuccess, showError } = useToast();
  const { t } = useTranslation();

  const {
    isOpen,
    config,
    primaryAction,
    secondaryAction,
    hideConfirmation,
    showDeleteAccount
  } = useConfirmationDialog();

  const handleDeleteClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmationText !== 'GOODBYE') {
      setError(t('product.deleteAccount.confirmation'));
      return;
    }
    setError('');

    // Show confirmation dialog
    showDeleteAccount(
      async () => {
        // On confirm - actually delete the account
        setLoading(true);
        try {
          await deleteUserAccount();
          showSuccess(t('product.deleteAccount.success'));
          setTimeout(() => {
          logout();
          }, 1500);
        } catch (error: any) {
          console.error('Error deleting account:', error);

          const errorMessage = error.response?.data?.error?.message ||
                              error.response?.data?.message ||
                              t('product.deleteAccount.error');

          showError(errorMessage);
          setError(errorMessage);
          setLoading(false);
        }
      },
      () => {
        // On cancel - just close dialog
        console.log('Delete cancelled');
      }
    );
  };

  const isDeleteEnabled = confirmationText === 'GOODBYE' && !loading;

  return (
    <>
      <div className="delete-account-container">
        <main className="delete-account-content">
          <div>

          </div>
          <h2 className="delete-account-warning-title">
            {t('product.deleteAccount.warning')}
          </h2>

          <ul className="delete-account-warning-list">
            <li>{t('product.deleteAccount.loseAccess')}</li>
            <li>{t('product.deleteAccount.deactivated')}</li>
            <li>{t('product.deleteAccount.undo')}</li>
            <li>{t('product.deleteAccount.permanent')}</li>
          </ul>

          <form onSubmit={handleDeleteClick} className="delete-account-form">
            <label className="delete-account-confirmation-label">
              {t('product.deleteAccount.confirmation')}
            </label>
            <input
              type="text"
              placeholder={t('product.deleteAccount.confirmationPlaceholder')}
              value={confirmationText}
              onChange={e => setConfirmationText(e.target.value)}
              className="delete-account-confirmation-input"
              required
              disabled={loading}
            />
            {error && <div className="delete-account-error">{error}</div>}

            <button
              type="submit"
              className={`delete-account-delete-btn ${isDeleteEnabled ? 'enabled' : 'disabled'}`}
              disabled={!isDeleteEnabled}
            >
              {loading ? t('common.loading') : t('product.deleteAccount.button')}
            </button>
          </form>

          <div className="delete-account-footer-text">©2025 AiCourses. All rights reserved.</div>
        </main>
      </div>

      {config && (
        <ConfirmationDialog
          isOpen={isOpen}
          onClose={hideConfirmation}
          type={config.type!}
          title={config.title}
          message={config.message}
          primaryButtonText={config.primaryButtonText!}
          secondaryButtonText={config.secondaryButtonText}
          onPrimaryAction={primaryAction!}
          onSecondaryAction={secondaryAction || undefined}
          showIcon={config.showIcon}
        />
      )}
    </>
  );
};

export default DeleteAccount;
